import React, { useState } from 'react';
import {TextField, Button, Box, Typography, Grid, CircularProgress, Breadcrumbs} from '@mui/material';
import api from '../../api';
import QuotaBlockedNotice from '../../components/subscription/QuotaBlockedNotice';
import {Link, useNavigate} from 'react-router-dom';
import {NumericFormat} from "react-number-format";
import DidNotLoad from "../../components/DidNotLoad";
import PrinterCatalogSelect from '../../components/printers/PrinterCatalogSelect';

function RegisterPrinterPage() {
    const [name, setName] = useState('');
    const [brand, setBrand] = useState('');
    const [model, setModel] = useState('');
    const [printerModelUuid, setPrinterModelUuid] = useState('');
    const [avgConsumption, setAvgConsumption] = useState('');
    const [consumption, setConsumption] = useState('');
    const [price, setPrice] = useState('');
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resultErrors, setResultErrors] = useState({
        name: null,
        brand: null,
        model: null,
        printer_model_uuid: null,
        avg_consumption_watts: null,
        price: null,
        consumption: null,
        has_errors: false
    });
    const [hasResponse, setHasResponse] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);

    const handleSubmit = (event) => {
        event.preventDefault();

        let hasError = false
        if (name === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                name: 'O nome da impressora é obrigatório para distinguir uma da outra.',
                has_errors: true,
            }));
        }

        if (!printerModelUuid) {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                printer_model_uuid: 'Selecione a marca e o modelo da impressora.',
                has_errors: true,
            }));
        }

        if (hasError) {
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        const priceInCents = parseFloat(price.replace(/[^\d]/g, ''));
        const today = new Date().toISOString().slice(0, 10);

        const newPrinter = {
            name,
            printer_model_uuid: printerModelUuid,
            avg_consumption_watts: parseInt(avgConsumption, 10),
            power_consumption_watts: parseInt(consumption, 10),
            acquisition_value: parseInt(priceInCents, 10),
            acquisition_date: today,
            hourly_printing_cost: 0,
        };

        api.post('printers', newPrinter)
            .then((response) => {
                setHasResponse(true);
                console.log('Impressora criada com sucesso:', response);
                navigate('/admin/printers');
            })
            .catch((error) => {
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                    setHasResponse(true);
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
                if (!hasResponse) {
                    setDidNotLoad(true);
                }
            });
    };

    if (didNotLoad) {
        return <DidNotLoad/>
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/printers')} to={'/admin/printers'}>
                    Impressoras
                </Link>
                <Typography color="text.primary">Adicionando impressora</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Adicionar Nova Impressora</Typography>
            <QuotaBlockedNotice quotaKey="printers" backTo="/admin/printers" />
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            label="Identificação (ex: Ender das luminárias)"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        {resultErrors.name && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.name}
                            </div>
                        )}
                    </Grid>
                    <PrinterCatalogSelect
                        brand={brand}
                        model={model}
                        printerModelUuid={printerModelUuid}
                        errors={resultErrors}
                        onChange={(next) => {
                            setBrand(next.brand || '');
                            setModel(next.model || '');
                            setPrinterModelUuid(next.printer_model_uuid || '');
                            if (next.avg_consumption_watts) {
                                setAvgConsumption(String(next.avg_consumption_watts));
                            }
                            if (next.max_consumption_watts) {
                                setConsumption(String(next.max_consumption_watts));
                            }
                        }}
                    />
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Consumo médio (W)"
                            fullWidth
                            type="number"
                            value={avgConsumption}
                            onChange={(e) => setAvgConsumption(e.target.value)}
                            inputProps={{ min: 1, step: 1 }}
                            inputMode="numeric"
                        />
                        {resultErrors.avg_consumption_watts && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.avg_consumption_watts}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Consumo máximo (W)"
                            fullWidth
                            type="number"
                            value={consumption}
                            onChange={(e) => setConsumption(e.target.value)}
                            inputProps={{ min: 1, step: 1 }}
                            inputMode="numeric"
                        />
                        {resultErrors.consumption || resultErrors.power_consumption_watts ? (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.consumption || resultErrors.power_consumption_watts}
                            </div>
                        ) : null}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            label="Valor pago"
                            fullWidth
                            customInput={TextField}
                            value={price}
                            onValueChange={(values) => setPrice(values.value)}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                            inputMode="numeric"
                        />
                        {resultErrors.price && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.price}
                            </div>
                        )}
                    </Grid>
                </Grid>
                <Box sx={{display: 'flex', justifyContent: 'flex-end', marginTop: 2}}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                        fullWidth
                    >
                        {isSubmitting ? 'Cadastrando...' : 'Cadastrar Impressora'}
                    </Button>
                </Box>
            </form>
        </div>

    );
}

export default RegisterPrinterPage;
